import { FC, useState } from 'react';
import dayjs from 'dayjs';
import { PersonsV3 } from '@frontend/api-person';
import { SMSDataV3 } from '@frontend/api-sms-data';
import { useMessageAction } from '@frontend/contact-actions-send-message';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Text, Avatar, SpinningLoader, Button, useAlert } from '@frontend/design-system';
import { useTaskFormPatient } from '../../../hooks';
import { Citation } from '../../../types';
import { citationContainerStyle, messageStyle, alignRight, noCitationContainerStyle } from './styles';

interface MessageCitationProps {
  locationId: string;
  sourceId?: string;
  citation?: Citation;
}

export const MessageCitation: FC<MessageCitationProps> = ({ locationId, sourceId, citation }) => {
  const { t } = useTranslation('task-center');
  const { error: errorAlert } = useAlert();

  const fetchGetSMS = SMSDataV3.Queries.useFetchGetSMS();
  const [isLoading, setIsLoading] = useState(false);

  const { onClick: openThread } = useMessageAction();
  const { patient, isLoading: isFetchingPatient } = useTaskFormPatient(citation?.patient_id);
  const date = citation?.created_at ? dayjs(citation?.created_at).format('MMM DD, YYYY, hh mm A') : '';
  const personName = patient ? PersonsV3.PersonHelpers.getFullName(patient) : t('Patient Name');

  const onClickView = async () => {
    setIsLoading(true);
    if (!sourceId) {
      setIsLoading(false);
      return;
    }

    try {
      const result = await fetchGetSMS({
        request: {
          smsId: sourceId,
          locationId,
        },
      });
      if (!result.sms.id) {
        // This should never happen, because the BE should return an error instead, but to be safe, error handling can
        // be added here as well.
        setIsLoading(false);
        return;
      }
      openThread({
        threadGroupId: locationId,
        threadId: result.sms.threadId,
        targetSmsData: {
          id: result.sms.id,
          createdAt: result.sms.createdAt,
        },
      });
    } catch (error) {
      // TODO: sending it to sentry would be a good idea?
      errorAlert(t('Unable to open message at this moment'));
    }
    setIsLoading(false);
  };

  if (!citation) {
    return (
      <div css={noCitationContainerStyle}>
        <Icon name='message' size={16} />{' '}
        <Text size='large' weight='bold'>
          {t('View Details in Inbox')}
        </Text>
        <Button variant='tertiary' loading={isLoading} css={alignRight} onClick={onClickView}>
          {t('View')}
        </Button>
      </div>
    );
  }

  return (
    <div css={citationContainerStyle} onClick={onClickView}>
      <span>
        <Icon name='inbox' size={16} />
        <Text size='large' weight='bold'>
          {t('Inbox')}
        </Text>
      </span>
      <span>
        {isFetchingPatient ? <SpinningLoader /> : personName && <Avatar name={personName} />}
        {citation?.description && (
          <Text size='large' css={messageStyle}>
            {citation?.description}
          </Text>
        )}
      </span>
      <span>
        {personName && (
          <Text size='medium' weight='bold'>
            {personName}
          </Text>
        )}
        <Text color='light' size='small' css={alignRight}>
          {date}
        </Text>
      </span>
    </div>
  );
};
